<template>
  <div class="company-history">
    <div class="d-flex align-items-center mb-4">
      <h5 class="mb-0">История изменений компании</h5>
      <b-button
        size="sm"
        class="ml-auto"
        variant="outline-secondary"
        style="width: 30px; height: 28px;"
        title="Обновить"
        :disabled="loading"
        @click="loadHistory"
      >
        <b-icon
          icon="arrow-repeat"
          scale="1.5"
          aria-label="Обновить"
          :animation="loading ? 'spin' : ''"
        ></b-icon>
      </b-button>
    </div>

    <div v-if="error" class="text-danger mb-3">
      Не удалось загрузить историю. Попробуйте обновить ещё раз.
    </div>

    <!-- Большой лоадер только при первой загрузке; при обновлении старый список остаётся на месте -->
    <div v-if="!loaded" class="company-history__state">
      <b-spinner variant="success" label="Загрузка"></b-spinner>
      <div class="mt-2 text-muted">Загружаем историю...</div>
    </div>

    <div v-else-if="!entries.length && !error" class="company-history__state text-muted">
      <em>У компании пока нет истории изменений.</em>
    </div>

    <div v-else-if="entries.length" class="company-history__list">
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="history-entry"
        :class="`history-entry--${entry.operation_name}`"
      >
        <div class="history-entry__head">
          <span class="history-entry__badge">{{ operationLabel(entry.operation_name) }}</span>
          <span class="history-entry__user">{{ entry.user ? entry.user.name : 'Система' }}</span>
          <span class="history-entry__date">{{ entry.created_at }}</span>
        </div>

        <div v-if="entry.rows.length" class="history-entry__body">
          <div v-for="row in entry.rows" :key="row.key" class="history-entry__row">
            <div class="history-entry__label">{{ row.label }}</div>
            <div class="history-entry__value">{{ row.value }}</div>
          </div>
        </div>
        <div v-else-if="entry.operation_name === 'deleted'" class="history-entry__body text-muted">
          Компания удалена
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_LOG_HISTORY, VOCABULAR } from '@/constants';

// Поля, которые не несут смысла для пользователя
const HIDDEN_FIELDS = ['id', 'created_at', 'updated_at', 'deleted_at'];

// Поля-флаги: показываем «Да / Нет» вместо 1 / 0
const BOOLEAN_FIELDS = [
  'in_russia',
  'is_transport_company',
  'is_b2c_rezident',
  'motivation_block_all',
  'motivation_only_escort',
];

const OPERATION_LABELS = {
  created: 'Создана',
  updated: 'Изменена',
  deleted: 'Удалена',
};

export default {
  props: {
    companyId: { type: Number, required: false },
    modelName: { type: String, default: 'App\\Models\\Company' },
    // Счётчик: при увеличении история перезагружается
    reload: { type: Number, default: 0 },
  },

  data() {
    return {
      loading: false,
      loaded: false,
      error: false,
      logs: [],
      requestCounter: 0,
    };
  },

  computed: {
    entries() {
      return this.logs
        .map(log => ({
          ...log,
          rows: this.buildRows(log),
        }))
        // Обновление без значимых полей (например, только updated_at) — шум
        .filter(entry => entry.operation_name !== 'updated' || entry.rows.length);
    },
  },

  watch: {
    reload() {
      this.loadHistory();
    },
    companyId() {
      // Другая компания — старый список показывать нельзя
      this.logs = [];
      this.loaded = false;
      this.loadHistory();
    },
  },

  mounted() {
    this.loadHistory();
  },

  methods: {
    loadHistory() {
      if (!this.companyId) {
        return;
      }

      // Защита от гонки: учитываем только ответ последнего запроса
      const requestId = ++this.requestCounter;
      this.loading = true;
      this.error = false;

      api.call('get', `${API_LOG_HISTORY}?model=${encodeURIComponent(this.modelName)}&id=${this.companyId}`)
        .then(({ data }) => {
          if (requestId !== this.requestCounter) return;
          this.logs = Array.isArray(data) ? data : Object.values(data || {});
          this.loading = false;
          this.loaded = true;
        })
        .catch(() => {
          if (requestId !== this.requestCounter) return;
          // При ошибке оставляем уже показанный список, если он был
          this.error = true;
          this.loading = false;
          this.loaded = true;
        });
    },

    operationLabel(operation) {
      return OPERATION_LABELS[operation] || operation;
    },

    buildRows(log) {
      const fields = log.fields_value;
      if (!fields || typeof fields !== 'object') {
        return [];
      }

      const isCreated = log.operation_name === 'created';

      return Object.keys(fields)
        .filter(key => !HIDDEN_FIELDS.includes(key))
        // При создании пустые поля — шум; при изменении null значит «значение очищено»
        .filter(key => !(isCreated && (fields[key] === null || fields[key] === '')))
        .map(key => ({
          key,
          label: VOCABULAR[key] || key,
          value: this.formatValue(key, fields[key]),
        }));
    },

    formatValue(key, value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      if (BOOLEAN_FIELDS.includes(key)) {
        return +value ? 'Да' : 'Нет';
      }
      return value;
    },
  },
};
</script>

<style scoped>
.company-history__state {
  padding: 3rem 1rem;
  text-align: center;
}

.company-history__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.history-entry {
  border: 1px solid #e3e6e8;
  border-left: 4px solid #9aa5ad;
  border-radius: 4px;
  background: #fff;
}

.history-entry--created { border-left-color: #28a745; }
.history-entry--updated { border-left-color: #17a2b8; }
.history-entry--deleted { border-left-color: #dc3545; }

.history-entry__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 14px;
  background: #f7f9fa;
  border-bottom: 1px solid #e3e6e8;
}

.history-entry__badge {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.history-entry--created .history-entry__badge { color: #28a745; }
.history-entry--updated .history-entry__badge { color: #17a2b8; }
.history-entry--deleted .history-entry__badge { color: #dc3545; }

.history-entry__user { font-weight: 600; }

.history-entry__date {
  margin-left: auto;
  color: #6c757d;
  font-size: 13px;
}

.history-entry__body { padding: 6px 14px; }

.history-entry--deleted .history-entry__body { padding: 10px 14px; }

.history-entry__row {
  display: flex;
  gap: 16px;
  padding: 6px 0;
  border-bottom: 1px dashed #eceff1;
}

.history-entry__row:last-child { border-bottom: 0; }

.history-entry__label {
  flex: 0 0 38%;
  max-width: 260px;
  color: #6c757d;
}

.history-entry__value {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}
</style>
